import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Badge,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  Radio,
  RadioGroup,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Edit,
  FilterList,
  Mail,
  Message,
  MoreHoriz,
  Phone,
  Search,
  Delete,
  Schedule,
  Error,
  Refresh,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import moment from 'moment';
import { format } from 'date-fns';
import { api } from '../../../services/authService';

const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case "sms":
      return <Message fontSize="small" />;
    case "email":
      return <Mail fontSize="small" />;
    case "voice":
      return <Phone fontSize="small" />;
    case "chat":
      return <Message fontSize="small" />;
    default:
      return <Message fontSize="small" />;
  }
};

const getInitials = (name) => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const ConversationsScheduledMessages = () => {
  const theme = useTheme();
  const [date, setDate] = useState(moment());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [selectedNurturingCampaign, setSelectedNurturingCampaign] = useState("all");
  const [messages, setMessages] = useState([]);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    accountId: "",
    campaignId: "",
    funnelId: "",
    leadId: "",
    channel: "sms",
    content: "",
    scheduledDate: moment().add(1, 'day'),
    scheduledTime: "09:00",
    useTemplate: false,
    templateId: "",
  });
  const [searchLeads, setSearchLeads] = useState("");
  const [step, setStep] = useState(1);
  const [view, setView] = useState("calendar");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusTab, setStatusTab] = useState("scheduled");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add state for dropdown data
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [leads, setLeads] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [nurturingCampaigns, setNurturingCampaigns] = useState([]);

  // Add state for action menu and edit dialog
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editScheduledTime, setEditScheduledTime] = useState(moment());
  const [isSaving, setIsSaving] = useState(false);
  
  // Column filters for list view
  const [columnFilters, setColumnFilters] = useState({
    recipient: '',
    scheduledDateStart: null,
    scheduledDateEnd: null,
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [accountsResponse, templatesResponse] = await Promise.all([
          api.get('/crm/accounts/'),
          api.get('/acs/message-templates/')
        ]);

        setAccounts(accountsResponse.data);
        setMessageTemplates(templatesResponse.data);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
        setError('Failed to load dropdown data');
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch campaigns when account changes
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (selectedAccount !== "all") {
        try {
          const response = await api.get('/crm/campaigns/', {
            params: {
              account: selectedAccount
            }
          });
          setCampaigns(response.data);
        } catch (err) {
          console.error('Error fetching campaigns:', err);
          setError('Failed to load campaigns');
        }
      } else {
        setCampaigns([]);
      }
      // Reset campaign and nurturing campaign selections
      setSelectedCampaign("all");
      setSelectedNurturingCampaign("all");
    };

    fetchCampaigns();
  }, [selectedAccount]);

  // Fetch nurturing campaigns when campaign changes
  useEffect(() => {
    const fetchNurturingCampaigns = async () => {
      if (selectedCampaign !== "all") {
        try {
          const response = await api.get('/acs/nurturing-campaigns/', {
            params: {
              crm_campaign: selectedCampaign
            }
          });
          setNurturingCampaigns(response.data);
        } catch (err) {
          console.error('Error fetching nurturing campaigns:', err);
          setError('Failed to load nurturing campaigns');
        }
      } else {
        setNurturingCampaigns([]);
      }
      // Reset nurturing campaign selection
      setSelectedNurturingCampaign("all");
    };

    fetchNurturingCampaigns();
  }, [selectedCampaign]);

  // Fetch leads when search query changes
  useEffect(() => {
    const fetchLeads = async () => {
      if (searchLeads) {
        try {
          const response = await api.get('/crm/leads/', {
            params: {
              search: searchLeads
            }
          });
          setLeads(response.data);
        } catch (err) {
          console.error('Error fetching leads:', err);
        }
      } else {
        setLeads([]);
      }
    };

    fetchLeads();
  }, [searchLeads]);

  // Fetch messages from the API only when nurturing campaign is selected
  const fetchMessages = async () => {
    if (selectedNurturingCampaign === "all") {
      setMessages([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
        search: searchQuery,
        status: selectedChannel !== "all" ? selectedChannel : undefined,
        nurturing_campaign: selectedNurturingCampaign,
      };

      // Only add date filters for calendar view
      if (view === "calendar") {
        params.start_date = date.startOf('day').toISOString();
        params.end_date = date.endOf('day').toISOString();
      }

      const response = await api.get('/acs/bulk-messages/', { params });
      setMessages(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.length,
      }));
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load scheduled messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages when filters change
  useEffect(() => {
    fetchMessages();
  }, [pagination.page, pagination.pageSize, searchQuery, selectedChannel, selectedNurturingCampaign, date, view]);

  const formattedDate = date.format("MMMM yyyy");

  const filteredMessages = messages?.filter((message) => {
    const messageDateTime = moment(message.scheduled_for);
    
    // Different filtering logic based on view
    if (view === "calendar") {
      // Calendar view: filter by selected date
      const isSameDay = messageDateTime.isSame(date, 'day');
      const matchesSearch =
        message.participant_lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        message.message_content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesChannel = selectedChannel !== "all" ? message.channel === selectedChannel : true;
      const matchesAccount = selectedAccount !== "all" ? message.account_name === selectedAccount : true;
      const matchesCampaign = selectedCampaign !== "all" ? message.campaign_name === selectedCampaign : true;
      const matchesNurturingCampaign = selectedNurturingCampaign !== "all" ? message.nurturing_campaign_name === selectedNurturingCampaign : true;

      return isSameDay && matchesSearch && matchesChannel && matchesAccount && matchesCampaign && matchesNurturingCampaign;
    } else {
      // List view: show all messages with search and filter options
      const matchesSearch =
        message.participant_lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        message.message_content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesChannel = selectedChannel !== "all" ? message.channel === selectedChannel : true;
      const matchesAccount = selectedAccount !== "all" ? message.account_name === selectedAccount : true;
      const matchesCampaign = selectedCampaign !== "all" ? message.campaign_name === selectedCampaign : true;
      const matchesNurturingCampaign = selectedNurturingCampaign !== "all" ? message.nurturing_campaign_name === selectedNurturingCampaign : true;

      // Column-specific filters
      const matchesRecipient = columnFilters.recipient === '' || 
        message.participant_lead_name.toLowerCase().includes(columnFilters.recipient.toLowerCase()) ||
        message.participant_lead_phone.includes(columnFilters.recipient) ||
        message.participant_lead_email.toLowerCase().includes(columnFilters.recipient.toLowerCase());

      const messageDateTime = moment(message.scheduled_for);
      const matchesScheduledDate = (!columnFilters.scheduledDateStart || messageDateTime.isSameOrAfter(columnFilters.scheduledDateStart, 'day')) &&
        (!columnFilters.scheduledDateEnd || messageDateTime.isSameOrBefore(columnFilters.scheduledDateEnd, 'day'));

      return matchesSearch && matchesChannel && matchesAccount && matchesCampaign && matchesNurturingCampaign && 
             matchesRecipient && matchesScheduledDate;
    }
  }) || [];

  const filteredLeads = leads?.filter(
    (lead) =>
      lead.name.toLowerCase().includes(searchLeads.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchLeads.toLowerCase()) ||
      lead.phone.toLowerCase().includes(searchLeads.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchLeads.toLowerCase())
  ) || [];

  const handlePreviousMonth = () => {
    setDate(date.clone().subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setDate(date.clone().add(1, 'month'));
  };

  const handleNewMessageChange = (field, value) => {
    setNewMessage((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectTemplate = (templateId) => {
    const template = messageTemplates.find((t) => t.id === templateId);
    if (template) {
      setNewMessage((prev) => ({
        ...prev,
        templateId,
        content: template.content,
        channel: template.channel,
      }));
    }
  };

  const handleScheduleMessage = () => {
    const lead = leads.find((l) => l.id === newMessage.leadId);
    if (!lead) return;

    const [hours, minutes] = newMessage.scheduledTime.split(":").map(Number);
    const scheduledDateTime = new Date(newMessage.scheduledDate);
    scheduledDateTime.setHours(hours, minutes, 0, 0);

    const newScheduledMessage = {
      id: (messages.length + 1).toString(),
      recipient: {
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
      },
      content: newMessage.content,
      scheduledDate: scheduledDateTime.toISOString(),
      channel: newMessage.channel,
      status: "scheduled",
      accountId: newMessage.accountId,
      campaignId: newMessage.campaignId,
      funnelId: newMessage.funnelId,
    };

    setMessages([...messages, newScheduledMessage]);
    setNewMessage({
      accountId: "",
      campaignId: "",
      funnelId: "",
      leadId: "",
      channel: "sms",
      content: "",
      scheduledDate: moment().add(1, 'day'),
      scheduledTime: "09:00",
      useTemplate: false,
      templateId: "",
    });
    setStep(1);
    setIsNewMessageOpen(false);
  };

  const resetNewMessageForm = () => {
    setNewMessage({
      accountId: "",
      campaignId: "",
      funnelId: "",
      leadId: "",
      channel: "sms",
      content: "",
      scheduledDate: moment().add(1, 'day'),
      scheduledTime: "09:00",
      useTemplate: false,
      templateId: "",
    });
    setStep(1);
  };

  const isNextStepDisabled = () => {
    if (step === 1) {
      return !newMessage.accountId || !newMessage.campaignId || !newMessage.funnelId || !newMessage.leadId;
    }
    if (step === 2) {
      return !newMessage.channel || !newMessage.content;
    }
    return false;
  };

  const getMessagesForDate = (day) => {
    return messages?.filter(message => {
      const messageDateTime = moment(message.scheduled_for);
      return messageDateTime.isSame(day, 'day');
    }) || [];
  };

  const renderCalendar = () => {
    return (
      <Card>
        <CardHeader
          title={formattedDate}
          action={
            <Box>
              <IconButton onClick={handlePreviousMonth}>
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={handleNextMonth}>
                <ChevronRight />
              </IconButton>
            </Box>
          }
        />
        <CardContent>
          <DateCalendar
            value={date}
            onChange={(newDate) => setDate(newDate)}
            renderDay={(day, _value, DayComponentProps) => {
              const messagesForDay = getMessagesForDate(day);
              return (
                <Badge
                  key={day.toString()}
                  overlap="circular"
                  badgeContent={messagesForDay.length > 0 ? messagesForDay.length : null}
                  color="primary"
                >
                  <Box
                    {...DayComponentProps}
                    sx={{
                      position: 'relative',
                      '& .MuiPickersDay-root': {
                        color: messagesForDay.length > 0 ? 'primary.main' : 'inherit',
                        fontWeight: messagesForDay.length > 0 ? 'bold' : 'normal',
                      },
                    }}
                  />
                </Badge>
              );
            }}
            sx={{
              '& .MuiPickersCalendarHeader-root': {
                display: 'none',
              },
              '& .MuiPickersDay-root': {
                color: 'text.primary',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <Button 
            size="small" 
            startIcon={<CalendarIcon />}
            onClick={() => setDate(moment())}
          >
            Today
          </Button>
          <Typography variant="body2" color="text.secondary">
            {filteredMessages.length} scheduled
          </Typography>
        </CardActions>
      </Card>
    );
  };

  const getMessagesByStatus = (status) => {
    switch (status) {
      case "scheduled":
        return messages.filter(msg => msg.status === "scheduled" || msg.status === "pending");
      case "sent":
        return messages.filter(msg => 
          ["sent", "delivered", "opened", "clicked", "replied"].includes(msg.status)
        );
      case "failed":
        return messages.filter(msg => 
          ["failed", "opted_out"].includes(msg.status)
        );
      case "retry":
        return messages.filter(msg => msg.status === "retry");
      default:
        return messages;
    }
  };

  const getStatusCount = (status) => {
    return getMessagesByStatus(status).length;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
      case "pending":
        return "default";
      case "sent":
      case "delivered":
      case "opened":
      case "clicked":
      case "replied":
        return "success";
      case "failed":
      case "opted_out":
        return "error";
      case "retry":
        return "warning";
      default:
        return "default";
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPaginatedMessages = (messages) => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return messages.slice(startIndex, endIndex);
  };

  // Action menu handlers
  const handleMenuOpen = (event, message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleEditClick = () => {
    console.log('Edit clicked, selectedMessage:', selectedMessage);
    if (selectedMessage) {
      setEditScheduledTime(moment(selectedMessage.scheduled_for));
      setIsEditDialogOpen(true);
      // Close the menu but don't clear selectedMessage yet
      setAnchorEl(null);
    } else {
      console.error('No selected message when edit was clicked');
    }
  };

  const handleDeleteClick = async () => {
    if (!selectedMessage) {
      console.log('No selected message to delete');
      return;
    }

    console.log('Delete message:', selectedMessage);
    console.log('Message group ID:', selectedMessage.message_group);

    try {
      // Delete the message using the API endpoint
      await api.delete(`/api/bulk-campaign-messages/delete_by_message_group/?message_group_id=${selectedMessage.message_group}`);

      // Remove the message from local state
      setMessages(prevMessages => 
        prevMessages.filter(msg => msg.id !== selectedMessage.id)
      );

      handleMenuClose();
    } catch (error) {
      console.error('Error deleting message:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to delete message: ${error.response?.data?.message || error.response?.data?.detail || error.message}`);
    }
  };

  const handleSaveEdit = async () => {
    console.log('handleSaveEdit called, selectedMessage:', selectedMessage);
    if (!selectedMessage) {
      console.log('No selected message');
      return;
    }

    setIsSaving(true);
    console.log('Saving edit for message:', selectedMessage.id);
    console.log('New scheduled time:', editScheduledTime.toISOString());

    try {
      // Update the message in the API
      const response = await api.patch(`/acs/bulk-messages/${selectedMessage.id}/`, {
        scheduled_for: editScheduledTime.toISOString()
      });

      console.log('API response:', response);
      console.log('Response data:', response.data);

      // Update the message in local state with the response data if available
      const updatedMessage = response.data || { ...selectedMessage, scheduled_for: editScheduledTime.toISOString() };
      
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === selectedMessage.id 
            ? updatedMessage
            : msg
        )
      );

      setIsEditDialogOpen(false);
      setSelectedMessage(null);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error updating scheduled time:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error status text:', error.response?.statusText);
      setError(`Failed to update scheduled time: ${error.response?.data?.message || error.response?.data?.detail || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Date range preset handlers
  const handleDatePreset = (preset) => {
    const now = moment();
    let startDate = null;
    let endDate = null;

    switch (preset) {
      case 'today':
        startDate = now.clone().startOf('day');
        endDate = now.clone().endOf('day');
        break;
      case 'tomorrow':
        startDate = now.clone().add(1, 'day').startOf('day');
        endDate = now.clone().add(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        startDate = now.clone().startOf('week');
        endDate = now.clone().endOf('week');
        break;
      case 'nextWeek':
        startDate = now.clone().add(1, 'week').startOf('week');
        endDate = now.clone().add(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        startDate = now.clone().startOf('month');
        endDate = now.clone().endOf('month');
        break;
      case 'nextMonth':
        startDate = now.clone().add(1, 'month').startOf('month');
        endDate = now.clone().add(1, 'month').endOf('month');
        break;
      case 'next7Days':
        startDate = now.clone().startOf('day');
        endDate = now.clone().add(6, 'days').endOf('day');
        break;
      case 'next30Days':
        startDate = now.clone().startOf('day');
        endDate = now.clone().add(29, 'days').endOf('day');
        break;
      case 'clear':
        startDate = null;
        endDate = null;
        break;
      default:
        return;
    }

    setColumnFilters(prev => ({
      ...prev,
      scheduledDateStart: startDate,
      scheduledDateEnd: endDate,
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search scheduled messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                sx={{
                  bgcolor: isFilterOpen ? 'action.selected' : 'transparent',
                }}
              >
                Filter
                {(selectedAccount !== "all" || selectedCampaign !== "all" || selectedNurturingCampaign !== "all" || selectedChannel !== "all") && (
                  <Badge
                    color="primary"
                    variant="dot"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsNewMessageOpen(true)}
              >
                Schedule Message
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Advanced Filter Section */}
        {isFilterOpen && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Advanced Filters</Typography>
              <Button
                size="small"
                onClick={() => {
                  setSelectedAccount("all");
                  setSelectedCampaign("all");
                  setSelectedNurturingCampaign("all");
                  setSelectedChannel("all");
                }}
              >
                Reset
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    label="Channel"
                  >
                    <MenuItem value="all">All Channels</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="voice">Voice</MenuItem>
                    <MenuItem value="chat">Chat</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Account</InputLabel>
                  <Select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    label="Account"
                  >
                    <MenuItem value="all">All Accounts</MenuItem>
                    {accounts?.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    )) || []}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    label="Campaign"
                    disabled={selectedAccount === "all"}
                  >
                    <MenuItem value="all">All Campaigns</MenuItem>
                    {campaigns?.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    )) || []}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Nurturing Campaign</InputLabel>
                  <Select
                    value={selectedNurturingCampaign}
                    onChange={(e) => setSelectedNurturingCampaign(e.target.value)}
                    label="Nurturing Campaign"
                    disabled={selectedCampaign === "all"}
                  >
                    <MenuItem value="all">All Nurturing Campaigns</MenuItem>
                    {nurturingCampaigns?.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    )) || []}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={statusTab} 
          onChange={(e, newValue) => setStatusTab(newValue)}
          aria-label="message status tabs"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule />
                Scheduled
                <Chip 
                  size="small" 
                  label={getStatusCount("scheduled")} 
                  color={getStatusColor("scheduled")}
                />
              </Box>
            } 
            value="scheduled" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Mail />
                Sent
                <Chip 
                  size="small" 
                  label={getStatusCount("sent")} 
                  color={getStatusColor("sent")}
                />
              </Box>
            } 
            value="sent" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Error />
                Failed
                <Chip 
                  size="small" 
                  label={getStatusCount("failed")} 
                  color={getStatusColor("failed")}
                />
              </Box>
            } 
            value="failed" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Refresh />
                Retry
                <Chip 
                  size="small" 
                  label={getStatusCount("retry")} 
                  color={getStatusColor("retry")}
                />
              </Box>
            } 
            value="retry" 
          />
        </Tabs>
      </Box>

      {/* View Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={view} onChange={(e, newValue) => setView(newValue)}>
          <Tab label="Calendar View" value="calendar" />
          <Tab label="List View" value="list" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Calendar View */}
          {view === "calendar" && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {renderCalendar()}
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                  {statusTab.charAt(0).toUpperCase() + statusTab.slice(1)} Messages for {format(date.toDate(), "MMMM d, yyyy")}
                </Typography>
                {getMessagesByStatus(statusTab).length === 0 ? (
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No {statusTab} messages for this date.
                      </Typography>
                      {statusTab === "scheduled" && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setIsNewMessageOpen(true)}
                        >
                          Schedule Message
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {getPaginatedMessages(getMessagesByStatus(statusTab)).map((message) => {
                      const messageDateTime = new Date(message.scheduled_for);
                      const formattedTime = format(messageDateTime, "h:mm a");

                      return (
                        <Card key={message.id} sx={{ mb: 2 }}>
                          <CardHeader
                            title={message.participant_lead_name}
                            subheader={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  size="small"
                                  icon={<ChannelIcon channel={message.channel} />}
                                  label={message.channel}
                                  variant="outlined"
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {formattedTime}
                                </Typography>
                              </Box>
                            }
                            action={
                              <IconButton onClick={(e) => handleMenuOpen(e, message)}>
                                <MoreHoriz />
                              </IconButton>
                            }
                          />
                          <CardContent>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {message.message_content}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={message.account_name}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={message.campaign_name}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={message.nurturing_campaign_name}
                                variant="outlined"
                              />
                            </Box>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {message.participant_lead_phone} • {message.participant_lead_email}
                            </Typography>
                            <Chip
                              size="small"
                              label={message.status}
                              color={getStatusColor(message.status)}
                            />
                          </CardActions>
                        </Card>
                      );
                    })}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <TablePagination
                        component="div"
                        count={getMessagesByStatus(statusTab).length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          )}

          {/* List View */}
          {view === "list" && (
            <Paper>
              {/* List View Header with Summary */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" color="text.primary">
                      All Scheduled Messages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Showing {filteredMessages.length} messages across all dates
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Chip 
                        size="small" 
                        label={`Scheduled: ${getStatusCount("scheduled")}`} 
                        color="default"
                      />
                      <Chip 
                        size="small" 
                        label={`Sent: ${getStatusCount("sent")}`} 
                        color="success"
                      />
                      <Chip 
                        size="small" 
                        label={`Failed: ${getStatusCount("failed")}`} 
                        color="error"
                      />
                      <Chip 
                        size="small" 
                        label={`Retry: ${getStatusCount("retry")}`} 
                        color="warning"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* List Headers */}
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10}>
                    <Grid container spacing={2} sx={{ fontWeight: 'medium' }}>
                      <Grid item xs={2}>Recipient</Grid>
                      <Grid item xs={1.5}>Message</Grid>
                      <Grid item xs={1}>Channel</Grid>
                      <Grid item xs={1}>Status</Grid>
                      <Grid item xs={1.5}>Scheduled Date</Grid>
                      <Grid item xs={1}>Created</Grid>
                      <Grid item xs={1}>Sent</Grid>
                      <Grid item xs={1.5}>Context</Grid>
                      <Grid item xs={1}>Actions</Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                        sx={{ minWidth: 'auto' }}
                      >
                        Filters
                      </Button>
                      {(columnFilters.recipient || columnFilters.scheduledDateStart || columnFilters.scheduledDateEnd) && (
                        <Chip
                          size="small"
                          label="Active"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              

              <Divider />
              {getPaginatedMessages(getMessagesByStatus(statusTab)).map((message) => {
                const createdDateTime = message.created_at ? new Date(message.created_at) : null;
                const scheduledDateTime = message.scheduled_for ? new Date(message.scheduled_for) : null;
                const sentDateTime = message.sent_at ? new Date(message.sent_at) : null;

                return (
                  <Box key={message.id}>
                    <Box sx={{ p: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={2}>
                          <Typography variant="body2" fontWeight="medium">
                            {message.participant_lead_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {message.participant_lead_phone}
                          </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                          <Typography variant="body2" noWrap>
                            {message.message_content}
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <Chip
                            size="small"
                            icon={<ChannelIcon channel={message.channel} />}
                            label={message.channel}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={1}>
                          <Chip
                            size="small"
                            label={message.status}
                            color={getStatusColor(message.status)}
                          />
                        </Grid>
                        <Grid item xs={1.5}>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {scheduledDateTime ? format(scheduledDateTime, "MMM d, yyyy") : '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {scheduledDateTime ? format(scheduledDateTime, "h:mm a") : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <Typography variant="body2" color="text.secondary">
                            {createdDateTime ? format(createdDateTime, "MMM d, h:mm a") : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <Typography variant="body2" color="text.secondary">
                            {sentDateTime ? format(sentDateTime, "MMM d, h:mm a") : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Account:
                              </Typography>
                              <Chip
                                size="small"
                                label={message.account_name}
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Campaign:
                              </Typography>
                              <Chip
                                size="small"
                                label={message.campaign_name}
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Nurturing:
                              </Typography>
                              <Chip
                                size="small"
                                label={message.nurturing_campaign_name}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, message)}>
                              <MoreHoriz />
                            </IconButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    <Divider />
                  </Box>
                );
              })}
              <TablePagination
                component="div"
                count={getMessagesByStatus(statusTab).length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Paper>
          )}
        </>
      )}

      {/* New Message Dialog */}
      <Dialog
        open={isNewMessageOpen}
        onClose={() => {
          setIsNewMessageOpen(false);
          resetNewMessageForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule New Message</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {step === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Account</InputLabel>
                    <Select
                      value={newMessage.accountId}
                      onChange={(e) => handleNewMessageChange("accountId", e.target.value)}
                      label="Account"
                    >
                      {accounts?.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      )) || []}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Campaign</InputLabel>
                    <Select
                      value={newMessage.campaignId}
                      onChange={(e) => handleNewMessageChange("campaignId", e.target.value)}
                      label="Campaign"
                    >
                      {campaigns?.map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </MenuItem>
                      )) || []}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Funnel</InputLabel>
                    <Select
                      value={newMessage.funnelId}
                      onChange={(e) => handleNewMessageChange("funnelId", e.target.value)}
                      label="Funnel"
                    >
                      {funnels?.map((funnel) => (
                        <MenuItem key={funnel.id} value={funnel.id}>
                          {funnel.name}
                        </MenuItem>
                      )) || []}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Search Leads"
                    value={searchLeads}
                    onChange={(e) => setSearchLeads(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                    <RadioGroup
                      value={newMessage.leadId}
                      onChange={(e) => handleNewMessageChange("leadId", e.target.value)}
                    >
                      {leads?.map((lead) => (
                        <Box
                          key={lead.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Radio value={lead.id} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {getInitials(lead.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{lead.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {lead.email} • {lead.phone}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {lead.company}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={lead.status}
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                        </Box>
                      )) || []}
                    </RadioGroup>
                  </Box>
                </Grid>
              </Grid>
            )}

            {step === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Channel</InputLabel>
                    <Select
                      value={newMessage.channel}
                      onChange={(e) => handleNewMessageChange("channel", e.target.value)}
                      label="Channel"
                    >
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="voice">Voice</MenuItem>
                      <MenuItem value="chat">Chat</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message"
                    value={newMessage.content}
                    onChange={(e) => handleNewMessageChange("content", e.target.value)}
                    helperText="Use {{name}}, {{company}}, etc. for personalization variables."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Date"
                    value={newMessage.scheduledDate}
                    onChange={(newValue) => handleNewMessageChange("scheduledDate", newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TimePicker
                    label="Time"
                    value={newMessage.scheduledDate}
                    onChange={(newValue) => handleNewMessageChange("scheduledDate", newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
            )}

            {step === 3 && (
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Account</Typography>
                    <Typography>
                      {accounts.find(a => a.id === newMessage.accountId)?.name || 'Not selected'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Campaign</Typography>
                    <Typography>
                      {campaigns.find(c => c.id === newMessage.campaignId)?.name || 'Not selected'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Nurturing Campaign</Typography>
                    <Typography>
                      {nurturingCampaigns.find(c => c.id === newMessage.campaignId)?.name || 'Not selected'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Lead</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Avatar>
                        {getInitials(leads.find(l => l.id === newMessage.leadId)?.name || "")}
                      </Avatar>
                      <Box>
                        <Typography>
                          {leads.find(l => l.id === newMessage.leadId)?.name || 'Not selected'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {leads.find(l => l.id === newMessage.leadId)?.email} •{" "}
                          {leads.find(l => l.id === newMessage.leadId)?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Message</Typography>
                      <Chip
                        size="small"
                        icon={<ChannelIcon channel={newMessage.channel} />}
                        label={newMessage.channel}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {newMessage.content}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">Scheduled For</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography>
                        {format(newMessage.scheduledDate, "PPP")} at{" "}
                        {format(newMessage.scheduledDate, "h:mm a")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {step > 1 && (
            <Button onClick={() => setStep(step - 1)}>Back</Button>
          )}
          {step < 3 ? (
            <Button
              variant="contained"
              onClick={() => setStep(step + 1)}
              disabled={isNextStepDisabled()}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleScheduleMessage}
            >
              Schedule Message
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Filter Side Panel */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: isFilterPanelOpen ? 0 : '-400px',
          width: '400px',
          height: '100vh',
          bgcolor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
          boxShadow: 3,
          zIndex: 1200,
          transition: 'right 0.3s ease-in-out',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" color="text.primary">Filters</Typography>
            <IconButton onClick={() => setIsFilterPanelOpen(false)}>
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Recipient Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
              Recipient
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, phone, or email..."
              value={columnFilters.recipient}
              onChange={(e) => setColumnFilters(prev => ({ ...prev, recipient: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Date Range Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
              Scheduled Date
            </Typography>
            
            {/* Date Presets */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Quick Presets
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={columnFilters.scheduledDateStart && columnFilters.scheduledDateEnd ? "contained" : "outlined"}
                    onClick={() => handleDatePreset('today')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('tomorrow')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Tomorrow
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('thisWeek')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    This Week
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('nextWeek')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Next Week
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('thisMonth')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    This Month
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('nextMonth')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Next Month
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('next7Days')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Next 7 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDatePreset('next30Days')}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                  >
                    Next 30 Days
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Custom Date Range */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Custom Range
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                  label="From Date"
                  value={columnFilters.scheduledDateStart}
                  onChange={(newValue) => setColumnFilters(prev => ({ ...prev, scheduledDateStart: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
                <DatePicker
                  label="To Date"
                  value={columnFilters.scheduledDateEnd}
                  onChange={(newValue) => setColumnFilters(prev => ({ ...prev, scheduledDateEnd: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Box>
            </Box>
          </Box>

          {/* Active Filters Summary */}
          {(columnFilters.recipient || columnFilters.scheduledDateStart || columnFilters.scheduledDateEnd) && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
                Active Filters
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {columnFilters.recipient && (
                  <Typography variant="body2" color="text.secondary">
                    Recipient: "{columnFilters.recipient}"
                  </Typography>
                )}
                {columnFilters.scheduledDateStart && (
                  <Typography variant="body2" color="text.secondary">
                    From: {format(columnFilters.scheduledDateStart.toDate(), "MMM d, yyyy")}
                  </Typography>
                )}
                {columnFilters.scheduledDateEnd && (
                  <Typography variant="body2" color="text.secondary">
                    To: {format(columnFilters.scheduledDateEnd.toDate(), "MMM d, yyyy")}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setColumnFilters({
                recipient: '',
                scheduledDateStart: null,
                scheduledDateEnd: null,
              })}
            >
              Clear All Filters
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setIsFilterPanelOpen(false)}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <Edit sx={{ mr: 1 }} />
          Edit Scheduled Time
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <Delete sx={{ mr: 1 }} />
          Delete Message
        </MenuItem>
      </Menu>

      {/* Edit Scheduled Time Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedMessage(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Scheduled Time</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Message to: {selectedMessage?.participant_lead_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Date"
                  value={editScheduledTime}
                  onChange={(newValue) => setEditScheduledTime(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="Time"
                  value={editScheduledTime}
                  onChange={(newValue) => setEditScheduledTime(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Current scheduled time: {selectedMessage?.scheduled_for ? 
                    format(new Date(selectedMessage.scheduled_for), "PPP 'at' h:mm a") : 'Not set'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsEditDialogOpen(false);
            setSelectedMessage(null);
          }} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationsScheduledMessages; 